import { useQuery } from '@tanstack/react-query'
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { AccessNotice, SeatNotice } from '../components/SeatNotice'

/**
 * `/p/{slug}/{date}/{label}?r=x,y,w,h` resolved to a page.
 *
 * The permalink is the citation's real subject and it is built out of things
 * that survive a re-ingest: the publication, the date it was printed, the
 * label the paper itself put on the page, and a bbox. None of those is a
 * database identifier, so re-running the pipeline renumbers nothing a footnote
 * already claims. Resolution is the server's job because only the server knows
 * which page carries that label in that edition.
 */

interface Resolved {
  page_id: string
  edition_id: string
}

export function PermalinkPage() {
  const { slug = '', date = '', label = '' } = useParams()
  const [params] = useSearchParams()
  const region = params.get('r')

  const resolved = useQuery({
    queryKey: ['resolve', slug, date, label],
    queryFn: () =>
      api.get<Resolved>(
        `/resolve/${encodeURIComponent(slug)}/${encodeURIComponent(date)}/${encodeURIComponent(label)}`,
      ),
    retry: false,
  })

  if (resolved.data) {
    /* The bbox keeps its commas. A citation is read by people as often as it
       is followed by a browser, and `1064%2C699%2C583%2C1191` is not a
       physical address anybody can check against a page. */
    const forward = [
      region ? `r=${region}` : null,
      params.get('q') ? `q=${encodeURIComponent(params.get('q') as string)}` : null,
    ]
      .filter(Boolean)
      .join('&')
    return <Navigate replace to={`/page/${resolved.data.page_id}${forward ? `?${forward}` : ''}`} />
  }

  if (resolved.isError) {
    return (
      <div className="mx-auto w-full max-w-[720px] p-6">
        <div className="chrome mb-2">Permalink</div>
        <h1 className="text-[32px] leading-[1.04]">This citation does not resolve</h1>
        <p className="mt-3 max-w-[64ch] text-[15.5px] leading-normal text-ink2">
          Nothing in this archive is filed under{' '}
          <span className="font-mono text-[13px] text-ink">
            {slug} / {date} / {label}
          </span>
          . Either the edition is outside the licence this institution holds, or the page label was
          transcribed differently from the way the paper printed it.
        </p>
        <div className="mt-4 max-w-[64ch]">
          <SeatNotice error={resolved.error} onRetry={() => void resolved.refetch()} />
          <AccessNotice error={resolved.error} />
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <Link to="/browse" className="btn-primary no-underline">
            Browse by date
          </Link>
          <Link to={`/search?q=${encodeURIComponent(date)}`} className="btn-secondary no-underline">
            Search for this date
          </Link>
        </div>
        {region && (
          <p className="ident mt-6 border-t border-n300 pt-3">
            The region this citation names is {region} in the page's raster pixels. It is data, not
            a rendering, so it will still address the same passage once the page is found.
          </p>
        )}
      </div>
    )
  }

  return (
    <p className="p-6 text-[15px] text-ink2">
      Resolving {slug} / {date} / {label}
      {region ? ` at region ${region}` : ''}.
    </p>
  )
}
